import React from 'react';
import {
    Celebration as CelebrationIcon,
    Restaurant as RestaurantIcon,
    HolidayVillage as HolidayVillageIcon,
    School as SchoolIcon,
    Favorite as FavoriteIcon,
    ShoppingCart as ShoppingCartIcon,
    CardGiftcard as CardGiftcardIcon,
    Star as StarIcon,
} from '@mui/icons-material';
import { createSvgIcon } from '@mui/material/utils';
import { CalendarEvent } from '../types';
import { CURRENT_SEASON_KEY } from '../seasons';

export const PumpkinIcon = createSvgIcon(
    React.createElement('path', {
        d: 'M12 2c.55 0 1 .45 1 1v1.1c3.84.45 6.8 3.51 6.98 7.37.94 1.34 1.02 3.12.16 4.54-.86 1.42-2.38 2.29-4.04 2.29-.44 0-.87-.06-1.28-.17-.79 1.15-2.08 1.87-3.82 1.87s-3.03-.72-3.82-1.87c-.41.11-.84.17-1.28.17-1.66 0-3.18-.87-4.04-2.29-.86-1.42-.78-3.2.16-4.54.18-3.86 3.14-6.92 6.98-7.37V3c0-.55.45-1 1-1m-4 7c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1m8 0c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1m-4 4.5c-1.66 0-3 1-3 2h6c0-1-1.34-2-3-2z'
    }),
    'Pumpkin'
);

export interface Holiday {
    name: string;
    dateStr: string;
    icon: React.ElementType;
    color: string;
}

/**
 * Calculates the date of the Nth occurrence of a specific day of the week in a given month and year.
 * @param year The year.
 * @param month The month (0-indexed).
 * @param dayOfWeek The day of the week (0=Sunday, 1=Monday, ...).
 * @param n The occurrence of the day of the week (e.g., 1 for 1st, 3 for 3rd).
 * @returns The calculated Date object.
 */
const getNthDayOfWeek = (year: number, month: number, dayOfWeek: number, n: number): Date => {
    const d = new Date(Date.UTC(year, month, 1));
    while (d.getUTCDay() !== dayOfWeek) {
        d.setUTCDate(d.getUTCDate() + 1);
    }
    d.setUTCDate(d.getUTCDate() + (n - 1) * 7);
    return d;
};

/**
 * Generates a list of holidays for a given hockey season (e.g., 2025-2026, 2026-2027).
 * @param startYear The starting year of the season.
 * @returns An array of Holiday objects.
 */
export const getHolidaysForSeason = (startYear: number): Holiday[] => {
    const endYear = startYear + 1;
    const createDate = (year: number, month: number, day: number) => {
        const monthStr = String(month + 1).padStart(2, '0');
        const dayStr = String(day).padStart(2, '0');
        return `${year}-${monthStr}-${dayStr}`;
    };

    const formatDateObj = (d: Date) => {
        const year = d.getUTCFullYear();
        const month = String(d.getUTCMonth() + 1).padStart(2, '0');
        const day = String(d.getUTCDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const laborDay = formatDateObj(getNthDayOfWeek(startYear, 8, 1, 1)); // 1st Monday of Sept
    const thanksgivingDate = getNthDayOfWeek(startYear, 10, 4, 4); // 4th Thursday of Nov
    const thanksgiving = formatDateObj(thanksgivingDate);
    const blackFridayDate = new Date(thanksgivingDate.getTime());
    blackFridayDate.setUTCDate(thanksgivingDate.getUTCDate() + 1);
    const blackFriday = formatDateObj(blackFridayDate);
    const mlkDay = formatDateObj(getNthDayOfWeek(endYear, 0, 1, 3)); // 3rd Monday of Jan
    const presidentsDay = formatDateObj(getNthDayOfWeek(endYear, 1, 1, 3)); // 3rd Monday of Feb

    return [
        { name: 'Labor Day', dateStr: laborDay, icon: CelebrationIcon, color: '#546e7a' },
        { name: 'Halloween', dateStr: createDate(startYear, 9, 31), icon: PumpkinIcon, color: '#e65100' },
        { name: 'Thanksgiving', dateStr: thanksgiving, icon: RestaurantIcon, color: '#d84315' },
        { name: 'Black Friday', dateStr: blackFriday, icon: ShoppingCartIcon, color: '#37474f' },
        { name: 'Christmas Eve', dateStr: createDate(startYear, 11, 24), icon: HolidayVillageIcon, color: '#2e7d32' },
        { name: 'Christmas Day', dateStr: createDate(startYear, 11, 25), icon: CardGiftcardIcon, color: '#c62828' },
        { name: 'New Year\'s Eve', dateStr: createDate(startYear, 11, 31), icon: CelebrationIcon, color: '#f57f17' },
        { name: 'New Year\'s Day', dateStr: createDate(endYear, 0, 1), icon: CelebrationIcon, color: '#f57f17' },
        { name: 'MLK Day', dateStr: mlkDay, icon: SchoolIcon, color: '#3949ab' },
        { name: 'Valentine\'s Day', dateStr: createDate(endYear, 1, 14), icon: FavoriteIcon, color: '#d81b60' },
        { name: 'Presidents\' Day', dateStr: presidentsDay, icon: StarIcon, color: '#1565c0' },
    ];
};

export const getSeasonStartYear = (seasonKey: string = CURRENT_SEASON_KEY): number => {
    const match = seasonKey.match(/_(\d{2})_\d{2}$/);
    if (match) {
        return 2000 + parseInt(match[1], 10);
    }
    return 2026;
};

/**
 * Converts holiday data into CalendarEvent objects for the regular season.
 * Generates only in-season holidays (Labor Day through Presidents' Day) up to the season cutoff.
 * Off-season holidays (e.g. July 4th) and future seasons (e.g. Christmas 2027 for a 2026-2027 season) are excluded.
 * 
 * @param seasonKeyOrYears Season key (e.g. 'pw_26_27'), start year number, or array of start years. Defaults to CURRENT_SEASON_KEY.
 * @returns An array of CalendarEvent objects for the holidays within regular season bounds.
 */
export const getHolidayEvents = (seasonKeyOrYears: string | number | number[] = CURRENT_SEASON_KEY): CalendarEvent[] => {
    let years: number[];
    if (typeof seasonKeyOrYears === 'string') {
        years = [getSeasonStartYear(seasonKeyOrYears)];
    } else if (Array.isArray(seasonKeyOrYears)) {
        years = seasonKeyOrYears;
    } else {
        years = [seasonKeyOrYears];
    }

    const seen = new Set<string>();
    const events: CalendarEvent[] = [];

    for (const startYear of years) {
        const seasonHolidays = getHolidaysForSeason(startYear);
        for (const holiday of seasonHolidays) {
            // Cutoff: only include holidays within the regular season bounds (up to March 8 of endYear)
            if (holiday.dateStr > `${startYear + 1}-03-08`) continue;

            const key = `${holiday.name}-${holiday.dateStr}`;
            if (seen.has(key)) continue;
            seen.add(key);

            events.push({
                summary: holiday.name,
                start: `${holiday.dateStr}T00:00:00`,
                end: `${holiday.dateStr}T23:59:59`,
                location: 'Holiday',
                isHoliday: true,
                color: holiday.color,
                icon: holiday.icon,
            });
        }
    }

    return events;
};